import {useState, useEffect, useRef, useCallback} from 'react'
import {ensureWorkletModule} from './noiseGateWorkletCode'

interface PreviewAudioLevel {
  level: number
  autoThreshold: number
}

/**
 * Monitors audio level and adaptive auto-threshold from a raw MediaStreamTrack.
 * Uses a standalone AudioContext + AudioWorkletNode ('rms-meter').
 *
 * The RMS and noise floor computation runs on the audio thread — identical
 * algorithm to the in-call noise gate worklet — so the meter and threshold
 * indicator behave the same in lobby and room.
 */
export function usePreviewAudioLevel(track: MediaStreamTrack | null): PreviewAudioLevel {
  const [level, setLevel] = useState(0)
  const [autoThreshold, setAutoThreshold] = useState(0)

  const audioCtxRef = useRef<AudioContext | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const workletRef = useRef<AudioWorkletNode | null>(null)
  const lastUpdateRef = useRef(0)

  const cleanup = useCallback(() => {
    if (workletRef.current) {
      workletRef.current.port.onmessage = null
      workletRef.current.disconnect()
      workletRef.current = null
    }
    sourceRef.current?.disconnect()
    sourceRef.current = null
  }, [])

  useEffect(() => {
    if (!track || track.readyState === 'ended') {
      cleanup()
      setLevel(0)
      setAutoThreshold(0)
      return
    }

    let cancelled = false

    async function setup() {
      if (!track) return

      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContext()
      }
      const ctx = audioCtxRef.current
      if (ctx.state === 'suspended') ctx.resume()

      await ensureWorkletModule(ctx)
      if (cancelled) return

      const source = ctx.createMediaStreamSource(new MediaStream([track]))
      const worklet = new AudioWorkletNode(ctx, 'rms-meter')

      source.connect(worklet)
      sourceRef.current = source
      workletRef.current = worklet

      worklet.port.onmessage = (e: MessageEvent) => {
        const now = performance.now()
        if (now - lastUpdateRef.current > 33) {
          lastUpdateRef.current = now
          setLevel(e.data.rms)
          setAutoThreshold(e.data.autoThreshold)
        }
      }
    }

    setup()

    return () => {
      cancelled = true
      cleanup()
    }
  }, [track, cleanup])

  useEffect(() => {
    return () => {
      cleanup()
      if (audioCtxRef.current) {
        audioCtxRef.current.close()
        audioCtxRef.current = null
      }
    }
  }, [cleanup])

  return {level, autoThreshold}
}
